/**
 * Fantasy Statblocks Daggerheart PC DOM — shared by the plugin capture path
 * and the board reconstruction fallback.
 *
 * Matches official FSB classes (Heading.svelte / PropertyLine.svelte /
 * Statblock.svelte): `.obsidian-statblock-plugin.statblock`, `.heading.statblock-heading.name`,
 * `.statblock-item-inline`, `.line`, `.property-name`, `.rule`.
 */

import {
  collectPcWeapons,
  weaponAttackHtml,
} from "./pcGear";
import { ensureAbilityTableInFsbHtml, isAbilityProperty } from "./pcAbilities";
import { resolvePcHopeCurrent, resolvePcHopeMax } from "./pcHope";

export const FSB_HTML_NOTE_SUFFIX = ".fsb.html";

const TRAITS = [
  "Agility",
  "Strength",
  "Finesse",
  "Instinct",
  "Presence",
  "Knowledge",
] as const;

const SKIP_REST = new Set([
  "name",
  "layout",
  "statblock",
  "tokenid",
  "statblockid",
  "token_id",
  "statblock_id",
  "path",
  "level",
  "tier",
  "proficiency",
  "class",
  "subclass",
  "ancestry",
  "community",
  "pronouns",
  "concept",
  "character_concept",
  "description",
  "agility",
  "strength",
  "finesse",
  "instinct",
  "presence",
  "knowledge",
  "marked_hp",
  "marked_fear",
  "marked_stress",
  "marked_armor",
  "type",
  "monster",
  "system",
  "image",
  "imagepath",
  "token",
  "position",
  "cssclasses",
  "cssclass",
  "weapon",
  "weapons",
  "features",
  "class_features",
  "feats",
  "abilities",
  "domain_cards",
  "motives",
  "tactics",
  "motives_and_tactics",
  "motives & tactics",
]);

const REST_FIELDS: Array<{ keys: string[]; label: string }> = [
  { keys: ["evasion"], label: "Evasion" },
  { keys: ["armor_name", "armor"], label: "Armor" },
  { keys: ["thresholds"], label: "Thresholds" },
  { keys: ["hp", "max_hp"], label: "HP" },
  { keys: ["stress", "max_stress"], label: "Stress" },
  { keys: ["hope", "hope_max", "max_hope"], label: "Hope" },
  { keys: ["primary_weapon"], label: "Primary Weapon" },
  { keys: ["secondary_weapon"], label: "Secondary Weapon" },
  { keys: ["experience", "experiences"], label: "Experience" },
  { keys: ["background"], label: "Background" },
  { keys: ["connections"], label: "Connections" },
  { keys: ["potion"], label: "Potion" },
  { keys: ["gold"], label: "Gold" },
  { keys: ["inventory"], label: "Inventory" },
];

export function fsbHtmlNoteKey(notePath: string): string {
  const path = notePath.trim();
  if (!path) return "";
  return path.endsWith(FSB_HTML_NOTE_SUFFIX) ? path : `${path}${FSB_HTML_NOTE_SUFFIX}`;
}

export function looksLikeFsbHtml(html: string): boolean {
  if (!html || html.length < 20) return false;
  return /class=["'][^"']*\b(statblock|property-name|statblock-heading)\b/i.test(html);
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function textOf(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => textOf(item))
      .filter(Boolean)
      .join(", ");
  }
  if (value && typeof value === "object") {
    const rec = value as Record<string, unknown>;
    const name = textOf(rec.name ?? rec.title ?? rec.text);
    const extra = textOf(rec.modifier ?? rec.value ?? rec.bonus);
    return [name, extra].filter(Boolean).join(" ");
  }
  return value == null ? "" : String(value).trim();
}

function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function withDiceLinks(raw: string): string {
  const escaped = escapeHtml(raw);
  return escaped.replace(/\b(\d+)?d(\d+)(?:\s*[+-]\s*\d+)?\b/gi, (match) => {
    const formula = match.replace(/\s+/g, "");
    return `<span class="atlas-dice-link dh-adversary-dice-btn" data-dh-roll="dice" data-dh-roll-payload="${escapeHtml(formula)}" role="button" tabindex="0" title="Click to roll ${escapeHtml(formula)}">${match}</span>`;
  });
}

const HARVEST_LABEL_TO_KEY: Record<string, string> = {
  class: "class",
  subclass: "subclass",
  ancestry: "ancestry",
  community: "community",
  features: "features",
  "domain cards": "domain_cards",
  pronouns: "pronouns",
  concept: "concept",
  "character concept": "character_concept",
  description: "description",
  motives: "motives",
  tactics: "tactics",
  "motives & tactics": "motives_and_tactics",
  "motives and tactics": "motives_and_tactics",
  experience: "experience",
  experiences: "experience",
  evasion: "evasion",
  armor: "armor",
  "armor name": "armor_name",
  thresholds: "thresholds",
  hp: "hp",
  stress: "stress",
  hope: "hope",
  agility: "agility",
  strength: "strength",
  finesse: "finesse",
  instinct: "instinct",
  presence: "presence",
  knowledge: "knowledge",
  "primary weapon": "primary_weapon",
  "secondary weapon": "secondary_weapon",
  level: "level",
  tier: "tier",
  proficiency: "proficiency",
};

export function harvestPcFieldsFromHtml(
  html: string,
  creature: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...creature };
  if (!html) return next;
  const re =
    /<span class="property-name">\s*([^<]+?):?\s*<\/span>\s*<span class="property-text">([^<]*)<\/span>/gi;
  let hit: RegExpExecArray | null;
  while ((hit = re.exec(html))) {
    const label = hit[1]!.replace(/:$/, "").trim().toLowerCase();
    const key = HARVEST_LABEL_TO_KEY[label];
    if (!key || textOf(next[key])) continue;
    const value = hit[2]?.trim();
    if (value) next[key] = value;
  }
  return next;
}

function motivesText(creature: Record<string, unknown>): string {
  const combined = textOf(
    creature.motives_and_tactics ?? creature["motives & tactics"],
  );
  if (combined) return combined;
  return [textOf(creature.motives), textOf(creature.tactics)]
    .filter(Boolean)
    .join(" — ");
}

function propertyLine(
  label: string,
  value: string,
  opts: {
    roll?: { kind: string; payload: string; title: string };
    dice?: boolean;
    extra?: string;
  } = {},
): string {
  if (!value) return "";
  const cls = slug(label);
  const rollAttrs = opts.roll
    ? ` data-dh-roll="${escapeHtml(opts.roll.kind)}" data-dh-roll-payload="${escapeHtml(opts.roll.payload)}" role="button" tabindex="0" title="${escapeHtml(opts.roll.title)}"`
    : "";
  const body = opts.dice ? withDiceLinks(value) : escapeHtml(value);
  return `<div class="statblock-item-container"><div class="line ${cls}" data-property="${escapeHtml(cls)}"${rollAttrs}><span class="property-name">${escapeHtml(label)}</span><span class="property-text">${body}</span>${opts.extra ?? ""}</div></div>`;
}

function inlineLine(label: string, value: string): string {
  if (!value) return "";
  const cls = slug(label);
  return `<div class="line ${cls}" data-property="${escapeHtml(cls)}"><span class="property-name">${escapeHtml(label)}</span><span class="property-text">${escapeHtml(value)}</span></div>`;
}

function traitModifier(creature: Record<string, unknown>, trait: string): number {
  const key = trait.toLowerCase();
  const raw = creature[key] ?? creature[trait];
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw >= 6 && raw <= 30) return Math.floor((raw - 10) / 2);
    return raw;
  }
  if (typeof raw === "string") {
    const m = /([+-]?\d+)/.exec(raw);
    return m ? parseInt(m[1]!, 10) : 0;
  }
  return 0;
}

export function resolveTraitFromText(text: string): { trait: string; mod: number } | null {
  const compact = text.replace(/\s+/g, " ").trim();
  if (!compact || compact.length > 72) return null;
  const head =
    /^(agility|strength|finesse|instinct|presence|knowledge)\b/i.exec(compact);
  if (!head?.[1]) return null;
  const trait = head[1][0]!.toUpperCase() + head[1].slice(1).toLowerCase();
  const parsed = /([+-]\d+)/.exec(compact);
  const mod = parsed ? parseInt(parsed[1]!, 10) : 0;
  return { trait, mod };
}

function traitInline(creature: Record<string, unknown>, trait: string): string {
  const raw = creature[trait.toLowerCase()] ?? creature[trait];
  const mod = raw == null || raw === "" ? 0 : traitModifier(creature, trait);
  const shown = mod >= 0 ? `+${mod}` : String(mod);
  const payload = `${trait}|${mod}`;
  const d12 = `<span class="atlas-dice-link dh-adversary-dice-btn" data-dh-roll="attr" data-dh-roll-payload="${escapeHtml(payload)}" role="button" tabindex="0" title="Click to roll Daggerheart Duality (2d12 ${shown}) for ${escapeHtml(trait)}">d12</span>`;
  return `<div class="line ${slug(trait)}" data-property="${escapeHtml(slug(trait))}" data-dh-roll="attr" data-dh-roll-payload="${escapeHtml(payload)}" role="button" tabindex="0" title="Click to roll Daggerheart Duality (2d12 ${shown}) for ${escapeHtml(trait)}"><span class="property-name">${escapeHtml(trait)}</span><span class="property-text">${escapeHtml(shown)}</span>${d12}</div>`;
}

function traitRowHtml(
  creature: Record<string, unknown>,
  traits: readonly string[],
  lead = false,
): string {
  const lines = traits.map((trait) => traitInline(creature, trait)).join("");
  return `<div class="statblock-item-container statblock-item-inline dh-pc-trait-row"${lead ? ' data-dh-trait-lead="1"' : ""}>${lines}</div>`;
}

export function buildFsbPcSheetHtml(
  creature: Record<string, unknown>,
  nameFallback = "Character",
): string {
  const name = textOf(creature.name) || nameFallback;
  const vitals = [
    inlineLine("Level", textOf(creature.level)),
    inlineLine("Tier", textOf(creature.tier)),
    inlineLine("Proficiency", textOf(creature.proficiency)),
  ]
    .filter(Boolean)
    .join("");
  const origin = [
    inlineLine("Class", textOf(creature.class)),
    inlineLine("Subclass", textOf(creature.subclass)),
    inlineLine("Ancestry", textOf(creature.ancestry)),
    inlineLine("Community", textOf(creature.community)),
  ]
    .filter(Boolean)
    .join("");

  const concept = textOf(
    creature.concept || creature.character_concept || creature.description,
  );
  const pronouns = textOf(creature.pronouns);
  const motives = motivesText(creature);

  const used = new Set<string>();
  const combatParts: string[] = [];
  const loreRest: string[] = [];
  const loreLabels = new Set([
    "Background",
    "Connections",
    "Potion",
    "Gold",
    "Inventory",
  ]);
  const combatLabels = new Set([
    "Evasion",
    "Armor",
    "Thresholds",
    "HP",
    "Stress",
    "Hope",
  ]);
  const weapons = collectPcWeapons(creature)
    .map((weapon) => weaponAttackHtml(weapon, creature))
    .join("");
  let experienceHtml = "";
  for (const field of REST_FIELDS) {
    const isWeapon = field.label.includes("Weapon");
    for (const key of field.keys) used.add(key.toLowerCase());
    if (isWeapon || isAbilityProperty(field.label)) continue;
    const parts = field.keys.map((key) => textOf(creature[key])).filter(Boolean);
    if (parts.length === 0) continue;
    const value =
      field.label === "Hope"
        ? `${resolvePcHopeCurrent(creature)} / ${resolvePcHopeMax(creature)}`
        : field.label === "HP" || field.label === "Stress"
          ? parts.length === 2
            ? `${parts[0]} / ${parts[1]}`
            : parts[0]!
          : parts.join(" · ");
    if (field.label === "Experience") {
      experienceHtml = `<div class="statblock-item-container dh-pc-experiences">${propertyLine(
        field.label,
        value,
        {
          dice: true,
          roll: {
            kind: "exp",
            payload: value,
            title: "Used from the duality popup",
          },
        },
      )}<hr class="dh-pc-section-rule" /></div>`;
      continue;
    }
    if (combatLabels.has(field.label)) {
      combatParts.push(inlineLine(field.label, value));
      continue;
    }
    if (loreLabels.has(field.label)) {
      loreRest.push(propertyLine(field.label, value, { dice: true }));
    }
  }
  for (const [key, raw] of Object.entries(creature)) {
    const lower = key.toLowerCase();
    if (used.has(lower) || SKIP_REST.has(lower) || isAbilityProperty(lower)) {
      continue;
    }
    const value = textOf(raw);
    if (!value) continue;
    const label = key
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    if (isAbilityProperty(label)) continue;
    loreRest.push(propertyLine(label, value, { dice: true }));
  }
  const combat = combatParts.length
    ? `<div class="statblock-item-container statblock-item-inline dh-pc-combat">${combatParts.join("")}</div>`
    : "";
  const traits = `${traitRowHtml(creature, TRAITS.slice(0, 3), true)}${traitRowHtml(creature, TRAITS.slice(3))}`;
  const stats = `<div class="dh-pc-stats">${traits}${weapons}${combat}</div>`;
  const startRule = `<hr class="rule dh-pc-trait-rule dh-pc-stats-rule-start" />`;
  const endRule = `<hr class="rule dh-pc-trait-rule dh-pc-stats-rule-end" data-dh-pc-stats-end="1" />`;
  const html = `<div class="obsidian-statblock-plugin statblock dh-pc-card" data-dh-pc-card="1"><div class="statblock-content"><div class="column"><div class="statblock-item-container heading-container dh-pc-identity"><div class="flex-container"><h1 class="heading statblock-heading name">${escapeHtml(name)}</h1></div></div>${vitals ? `<div class="statblock-item-container statblock-item-inline dh-pc-vitals">${vitals}</div>` : ""}${origin ? `<div class="statblock-item-container statblock-item-inline dh-pc-origin">${origin}</div>` : ""}${propertyLine("Pronouns", pronouns)}${propertyLine("Concept", concept)}${propertyLine("Motives & Tactics", motives)}${startRule}${stats}${endRule}${experienceHtml}${loreRest.join("")}</div></div></div>`;
  return ensureAbilityTableInFsbHtml(html, creature);
}

const TRAIT_NAME_RE = /^(agility|strength|finesse|instinct|presence|knowledge):?$/i;
const RULE_BLOCK_RE =
  /<div class="statblock-item-container">\s*<hr[^>]*class="(?:[^"]*\s)?rule(?:\s[^"]*)?"[^>]*\/?>\s*<\/div>/gi;
/** Token `rule` only — must not strip sandwich / abilities rules. */
const RULE_HR_RE =
  /<hr[^>]*class="(?![^"]*\b(?:dh-pc-abilities-rule|dh-pc-trait-rule|dh-pc-stats-rule-end|dh-pc-section-rule)\b)(?:[^"]*\s)?rule(?:\s[^"]*)?"[^>]*\/?>/gi;
const TRAIT_CONTAINER_RE =
  /<div class="statblock-item-container[^"]*"[^>]*>\s*<div class="line[^"]*\b(agility|strength|finesse|instinct|presence|knowledge)\b/i;

function traitLabel(raw: string): boolean {
  return TRAIT_NAME_RE.test(raw.trim().replace(/:$/, ""));
}

function leftoverDividerEls(root: ParentNode): Element[] {
  return [...root.querySelectorAll("hr.rule, hr.tapered-rule, .tapered-rule")].filter(
    (el) =>
      !el.classList.contains("dh-pc-abilities-rule") &&
      !el.classList.contains("dh-pc-trait-rule") &&
      !el.classList.contains("dh-pc-stats-rule-end") &&
      !el.classList.contains("dh-pc-section-rule") &&
      !el.closest(".dh-pc-abilities") &&
      !el.closest(".dh-pc-stats"),
  );
}

function isTraitLine(el: Element): boolean {
  const prop = el.getAttribute("data-property") || "";
  if (traitLabel(prop)) return true;
  const cls = typeof el.className === "string" ? el.className : "";
  if (/\b(agility|strength|finesse|instinct|presence|knowledge)\b/i.test(cls)) return true;
  const name =
    el.querySelector?.(".property-name")?.textContent?.trim() ||
    (el.matches?.(".property-name") ? el.textContent?.trim() : "") ||
    "";
  return traitLabel(name);
}

function placeRuleWithDom(html: string): string | null {
  const doc = new DOMParser().parseFromString(`<div id="fsb-root">${html}</div>`, "text/html");
  const root = doc.getElementById("fsb-root");
  if (!root) return null;
  const lines = [
    ...root.querySelectorAll(".line, [data-property], .property-name"),
  ];
  const firstTrait = lines.find((el) => isTraitLine(el));
  if (!firstTrait) return html;
  const anchor =
    firstTrait.closest(".statblock-item-inline, .statblock-item-container") ??
    firstTrait;
  if (anchor instanceof HTMLElement) {
    anchor.setAttribute("data-dh-trait-lead", "1");
  }
  for (const hr of leftoverDividerEls(root)) {
    const wrap = hr.parentElement;
    hr.remove();
    if (
      wrap &&
      wrap.classList.contains("statblock-item-container") &&
      wrap.childElementCount === 0 &&
      !(wrap.textContent || "").trim()
    ) {
      wrap.remove();
    }
  }
  const wrap = doc.createElement("div");
  wrap.className = "statblock-item-container";
  const hr = doc.createElement("hr");
  hr.className = "rule dh-pc-trait-rule";
  wrap.appendChild(hr);
  anchor.parentNode?.insertBefore(wrap, anchor);
  return root.innerHTML.trim();
}

function placeRuleWithRegex(html: string): string {
  const stripped = html.replace(RULE_BLOCK_RE, "").replace(RULE_HR_RE, "");
  const byClass = TRAIT_CONTAINER_RE.exec(stripped);
  if (byClass?.index != null) {
    const rule = `<div class="statblock-item-container"><hr class="rule dh-pc-trait-rule" /></div>`;
    return `${stripped.slice(0, byClass.index)}${rule}${stripped.slice(byClass.index)}`;
  }
  const byLabel = /<span class="property-name">\s*(agility|strength|finesse|instinct|presence|knowledge):?\s*<\/span>/i.exec(
    stripped,
  );
  if (!byLabel || byLabel.index == null) return html;
  const containerAt = stripped.lastIndexOf('<div class="statblock-item-container', byLabel.index);
  const insertAt = containerAt >= 0 ? containerAt : byLabel.index;
  const rule = `<div class="statblock-item-container"><hr class="rule dh-pc-trait-rule" /></div>`;
  return `${stripped.slice(0, insertAt)}${rule}${stripped.slice(insertAt)}`;
}

/** Live DOM: move the traits divider above Agility and mark the lead group. */
export function placeFsbRuleAboveTraitsInDom(container: HTMLElement): void {
  if (container.querySelector("[data-dh-pc-card='1']")) return;
  const lines = [
    ...container.querySelectorAll(".line, [data-property], .property-name"),
  ];
  const firstTrait = lines.find((el) => isTraitLine(el));
  if (!firstTrait) return;
  const anchor =
    firstTrait.closest(".statblock-item-inline, .statblock-item-container") ??
    firstTrait;
  if (anchor instanceof HTMLElement) {
    anchor.setAttribute("data-dh-trait-lead", "1");
  }
  for (const hr of leftoverDividerEls(container)) {
    const wrap = hr.parentElement;
    hr.remove();
    if (
      wrap &&
      wrap.classList.contains("statblock-item-container") &&
      wrap.childElementCount === 0 &&
      !(wrap.textContent || "").trim()
    ) {
      wrap.remove();
    }
  }
  const wrap = container.ownerDocument.createElement("div");
  wrap.className = "statblock-item-container";
  const hr = container.ownerDocument.createElement("hr");
  hr.className = "rule dh-pc-trait-rule";
  wrap.appendChild(hr);
  anchor.parentNode?.insertBefore(wrap, anchor);
}

/** Obsidian DH PC: the rule sits immediately above Agility/Strength/Finesse. */
export function placeFsbRuleAboveTraits(html: string): string {
  if (!html || !html.includes("<")) return html;
  if (/data-dh-pc-card/.test(html) && /data-dh-pc-stats-end/.test(html)) return html;
  try {
    if (typeof DOMParser !== "undefined") {
      const placed = placeRuleWithDom(html);
      if (placed) return placed;
    }
  } catch {
    /* fall through */
  }
  return placeRuleWithRegex(html);
}

const ALLOWED_TAGS = new Set([
  "DIV",
  "SPAN",
  "H1",
  "H2",
  "H3",
  "H4",
  "P",
  "HR",
  "UL",
  "OL",
  "LI",
  "STRONG",
  "EM",
  "B",
  "I",
  "BR",
  "TABLE",
  "TBODY",
  "THEAD",
  "TR",
  "TD",
  "TH",
  "SMALL",
  "SUP",
  "SUB",
  "A",
  "SECTION",
  "ARTICLE",
  "IMG",
]);

const ALLOWED_ATTR = new Set([
  "class",
  "id",
  "role",
  "tabindex",
  "title",
  "style",
  "href",
  "src",
  "alt",
  "width",
  "height",
]);

function isAllowedAttr(name: string): boolean {
  const n = name.toLowerCase();
  return ALLOWED_ATTR.has(n) || n.startsWith("data-");
}

function sanitizeWithDom(html: string): string {
  const doc = new DOMParser().parseFromString(`<div id="fsb-root">${html}</div>`, "text/html");
  const root = doc.getElementById("fsb-root");
  if (!root) return "";
  const walk = (el: Element) => {
    for (const child of [...el.children]) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        const parent = child.parentNode;
        if (parent) {
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
        }
        continue;
      }
      for (const attr of [...child.attributes]) {
        const n = attr.name.toLowerCase();
        if (n.startsWith("on") || !isAllowedAttr(n)) {
          child.removeAttribute(attr.name);
          continue;
        }
        if ((n === "href" || n === "src") && /^\s*javascript:/i.test(attr.value)) {
          child.removeAttribute(attr.name);
        }
        if (n === "style" && /expression|url\s*\(\s*javascript/i.test(attr.value)) {
          child.removeAttribute(attr.name);
        }
      }
      walk(child);
    }
  };
  walk(root);
  root
    .querySelectorAll(
      ".dh-pc-controls, .dh-adversary-controls, .path-a-statblock-titlebar, .path-a-dice-tray",
    )
    .forEach((el) => el.remove());
  return root.innerHTML.trim();
}

function sanitizeWithRegex(html: string): string {
  return html
    .replace(/<script\b[\s\S]*?<\/script>/gi, "")
    .replace(/<style\b[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe\b[\s\S]*?<\/iframe>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "")
    .trim();
}

export function sanitizeFsbHtml(html: string): string {
  if (!html || !html.includes("<")) return "";
  try {
    if (typeof DOMParser !== "undefined") {
      return sanitizeWithDom(html);
    }
  } catch {
    /* fall through */
  }
  return sanitizeWithRegex(html);
}

export function pickFsbPcHtml(
  captured: string | null | undefined,
  creature: Record<string, unknown>,
  nameFallback: string,
): string {
  const clean = captured ? sanitizeFsbHtml(captured) : "";
  const harvested = harvestPcFieldsFromHtml(clean, creature);
  return buildFsbPcSheetHtml(harvested, nameFallback);
}
